import React, {ReactNode, useCallback, useMemo, useRef, useState} from "react"
import {ConditionsI, getInitConditionsValue, InputWrapperProps} from "./Interfaces"
import classNames from "classnames"
import styles from "./Input.module.scss"
import {Icon} from "../Icon/Icon"
import {useConditionsManage} from "./useConditionsManage"
import {ReactComponent as CheckIcon} from "./graphics/check.svg"
import {ReactComponent as CrossIcon} from "./graphics/cross.svg"
import {Button} from "../Button/Button"
import {MonochromeIcon} from "../MonochromeIcon/MonochromeIcon"
import {useTranslation} from "react-i18next"
import {usePasswordGenerator} from "./usePasswordGenerator"

export const InputWrapper: React.FC<{children?: ReactNode} & InputWrapperProps> = ({
    style,
    className,
    fieldClassName,
    label,
    type,
    noTopPadding,
    error,
    warning,
    labelCover,
    loader,
    labelHtmlFor,
    children,
    fullWidth,
    password,
    align,
    miniSize,
    underlineChildren,
    withoutUnderline,
    name,
    value,
    conditionsIsVisible = true,
    validationDataTest,
    errorUnderline,
    hideErrorText,
}) => {
    const {t} = useTranslation(["person"])
    const {generatePassword} = usePasswordGenerator()

    const isPasswordInput = password || type === "secured"
    const [showPassword, setShowPassword] = useState(isPasswordInput ? false : undefined)

    const initConditionsValue = useMemo(getInitConditionsValue, [])
    const [conditions, setConditions] = useState<ConditionsI>(initConditionsValue)

    const inputRef = useRef<HTMLDivElement>(null)

    useConditionsManage(isPasswordInput, conditions, setConditions, value)

    const handleGeneratePassword = useCallback(() => {
        if (!isPasswordInput) return

        const generatedPassword = generatePassword()
        setShowPassword(true)

        // Using native setter + dispatchEvent to programmatically set password value.
        // This ensures React and redux-form detect the change correctly.
        // Cannot just call onChange because Redux Form expects a real input event.
        const inputElement = inputRef.current?.querySelector("input")
        if (!inputElement) return

        const nativeValueSetter = Object.getOwnPropertyDescriptor(
            HTMLInputElement.prototype,
            "value"
        )?.set
        nativeValueSetter?.call(inputElement, generatedPassword)
        inputElement.dispatchEvent(new Event("input", {bubbles: true}))
    }, [isPasswordInput, generatePassword, setShowPassword])

    return (
        <div
            ref={inputRef}
            data-cy={"input"}
            style={style}
            className={classNames(styles["input-component"], className, {
                [styles["-withLabel"]]: label,
                [styles["-hidden"]]: type === "hidden",
                [styles["-no-tp"]]: noTopPadding,
                [styles["-fullWidth"]]: fullWidth,
                [styles.miniSize]: miniSize,
                [styles[`-align-${align}`]]: align,
                [styles.secured]: (password || type === "secured") && !showPassword,
            })}
        >
            <div
                className={classNames(styles["input-field"], fieldClassName, {
                    [styles["-error"]]: error,
                    [styles.labelCover]: labelCover,
                    [styles["-loader"]]: loader,
                    [styles.password]: type === "password" || type === "secured" || password,
                })}
            >
                {(type === "secured" || password) && (
                    <button
                        className={styles.passwordToggle}
                        name={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword(!showPassword)}
                        type={"button"}
                        tabIndex={-1}
                    >
                        <Icon name={showPassword ? "openEye" : "closedEye"} />
                    </button>
                )}

                {React.Children.map(children, (child) =>
                    React.cloneElement(child as React.ReactElement, {
                        type: password ? (showPassword ? "text" : "password") : type,
                    })
                )}

                {!withoutUnderline && (
                    <>
                        <div className={styles.underline}>{underlineChildren}</div>
                        <div className={styles.focusUnderline} />
                        <div
                            className={classNames(
                                styles.errorUnderline,
                                errorUnderline && styles.errorUnderlineVisible
                            )}
                        />
                    </>
                )}
                {!!label && (
                    <label className={classNames(styles.label)} htmlFor={labelHtmlFor}>
                        {label}
                    </label>
                )}
                {isPasswordInput && conditionsIsVisible && (
                    <div className={styles.passwordTooltip}>
                        <Button
                            containerClassName={styles.generateBtn}
                            monochromeIcon={<MonochromeIcon.Lock />}
                            text
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleGeneratePassword}
                        >
                            {t("person:ui.generate_password")}
                        </Button>
                        {Object.values(conditions).map((item) => (
                            <div key={item.title} className={styles.conditionItem}>
                                {item.isAccept ? <CheckIcon /> : <CrossIcon />}
                                <span
                                    className={styles.text}
                                    style={{
                                        color: item.isAccept ? "#444444" : "#666666",
                                    }}
                                    dangerouslySetInnerHTML={{__html: item.title}}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {loader && <div className={styles["input-loader"]} />}
            </div>
            {hideErrorText ? null : (
                <div
                    className={classNames(styles.errorText, {
                        [styles["-visible"]]: error,
                    })}
                    data-test={validationDataTest || (name ? name + "-error" : undefined)}
                >
                    {error}
                </div>
            )}
            <div
                className={classNames(styles.warningText, {
                    [styles["-visible"]]: warning && !error,
                })}
                data-test={name ? name + "-warning" : undefined}
            >
                {warning}
            </div>
        </div>
    )
}
